import type { OrderPriceResponseDto } from "Infrastructure/Api/Api";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useCreateOrderPriceMutation } from "Order/Modules/Price/Api/Mutation/useCreateOrderPriceMutation";
import {
	getOrderPriceEditFormSchema,
	type OrderPriceEditFormModel,
} from "Order/Modules/Price/Components/Form/Schema/orderPriceEditFormSchema";
import { useUser } from "Settings/User/Api/Query/useUserProfileQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

export const useOrderPriceEditForm = (
	orderPrice: OrderPriceResponseDto,
	orderId: number,
	setIsPriceModalOpen?: (isOpen: boolean) => void,
) => {
	const formDefaultValues = {
		price: orderPrice?.price,
		materialPrice: orderPrice?.materialPrice,
	};

	const currentUserId = useUser()?.user?.id;

	const form = useForm<OrderPriceEditFormModel>({
		resolver: zodResolver(getOrderPriceEditFormSchema()),
		defaultValues: formDefaultValues,
	});

	const { mutate: createOrderPrice, isPending } =
		useCreateOrderPriceMutation(orderId);
	const { handleServerError } = useHandleServerError(form);

	useEffect(() => {
		form.reset(formDefaultValues);
	}, [orderPrice]);

	const handleSubmitUpdate = () => {
		handleServerError((data) =>
			createOrderPrice(
				{ ...data, userId: currentUserId },
				{
					onSuccess: () => {
						setIsPriceModalOpen?.(false);
					},
				},
			),
		);
	};

	return {
		form,
		isPending,
		handleSubmitUpdate,
	};
};
